import React from 'react';
import { ShoppingCart, ExternalLink } from 'lucide-react';
import { Button } from './Button';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { products } from '../data/products';
import { useShopifyPrice } from '../hooks/useShopifyPrice';

export function ChatProductCard({ product }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  // Get full product data from products.js
  const fullProduct = products[product.id];
  
  // Fetch Shopify price and variantId for non-bundle products
  const { variantId, price, loading } = useShopifyPrice(product.id === 'bundle' || product.isBundle ? null : product.id);

  const handleAddToCart = () => {
    // For bundle, navigate to bundle page instead of adding to cart
    if (product.id === 'bundle' || product.isBundle) {
      navigate('/bundle');
      return;
    }
    
    if (fullProduct && variantId) {
      // Use Shopify price if available, otherwise use default
      const productWithPrice = {
        ...fullProduct,
        price: price || fullProduct.price
      };
      addToCart(productWithPrice, 1, variantId);
    }
  };

  const handleViewProduct = () => {
    // For bundle, navigate to bundle page
    if (product.id === 'bundle' || product.isBundle) {
      navigate('/bundle');
      return;
    }
    
    // Navigate to the product page using the correct route
    navigate(`/products/${product.id}`);
  };

  return (
    <div className="border p-3 sm:p-4 my-2 w-full min-w-[280px]" style={{ background: '#FAF9F6', borderColor: 'rgba(26,42,26,0.1)' }}>
      <div className="flex gap-3 sm:gap-4">
        {/* Product Image */}
        <div className="flex-shrink-0">
          <img 
            src={product.image} 
            alt={product.title}
            className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
          />
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium mb-1 text-sm sm:text-base" style={{ color: '#1A2A1A' }}>
            {product.title}
          </h4>
          <p className="text-xs sm:text-sm mb-2 line-clamp-2" style={{ color: 'rgba(26,42,26,0.52)' }}>
            {product.subtitle}
          </p>
          {product.id === 'bundle' || product.isBundle ? (
            <div className="mb-2 sm:mb-3">
              <p className="text-base sm:text-lg font-medium" style={{ color: '#B8704D' }}>
                ${product.price}
              </p>
              {product.subscriptionPrice && (
                <p className="text-xs sm:text-sm font-medium" style={{ color: '#4A7C59' }}>
                  or ${product.subscriptionPrice} with subscription
                </p>
              )}
            </div>
          ) : (
            <p className="text-base sm:text-lg font-medium mb-2 sm:mb-3" style={{ color: '#B8704D' }}>
              ${product.price}
            </p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        {product.id === 'bundle' || product.isBundle ? (
          // Bundle: Single "View Bundle" button
          <Button
            onClick={handleViewProduct}
            variant="primary"
            className="col-span-2 text-xs sm:text-sm py-2 justify-center whitespace-nowrap"
            style={{ background: '#1A2A1A', color: '#FAF9F6' }}
          >
            <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
            View Bundle
          </Button>
        ) : (
          // Regular products: Two buttons side by side
          <>
            <Button
              onClick={handleAddToCart}
              variant="primary"
              className="text-xs py-2 px-2 justify-center whitespace-nowrap"
              disabled={loading || !variantId}
              style={{ background: '#1A2A1A', color: '#FAF9F6' }}
            >
              <ShoppingCart className="w-3 h-3 mr-1" />
              {loading ? 'Loading...' : 'Add to Cart'}
            </Button>
            <Button
              onClick={handleViewProduct}
              variant="outline"
              className="text-xs py-2 px-2 justify-center whitespace-nowrap"
              style={{ borderColor: 'rgba(26,42,26,0.16)', color: '#1A2A1A', background: 'transparent' }}
            >
              <ExternalLink className="w-3 h-3 mr-1" />
              View Details
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
